import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LOGO_DARK_BG } from '../config/branding';

const VertriebSetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Ungültiger Einladungslink');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople/auth/set-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, password })
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.detail || 'Fehler beim Setzen des Passworts');
        return;
      }
      
      setSuccess(true);
      setEmail(data.email);
    } catch (err) {
      setError('Verbindungsfehler. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Account aktiviert!</h1>
          <p className="text-gray-600 mb-6">
            Ihr Passwort wurde erfolgreich gesetzt. Sie können sich jetzt mit Ihrer E-Mail-Adresse einloggen.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="text-sm text-gray-500">E-Mail:</div>
            <div className="font-semibold text-gray-900">{email}</div>
          </div>
          <Button
            onClick={() => navigate('/vertrieb/login')}
            className="w-full bg-brand-green hover:bg-brand-green-dark text-white"
          >
            Zum Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo with dark background */}
        <div className="text-center mb-6">
          <div className="bg-slate-800 rounded-xl p-4 inline-block mb-3">
            <img src={LOGO_DARK_BG} alt="Dein Gutachten Direkt" className="h-12" />
          </div>
          <p className="text-gray-300">Vertriebsteam</p>
        </div>

        <div className="bg-white rounded-2xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-brand-green" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Passwort setzen</h2>
          <p className="text-gray-600">
            Setzen Sie ein sicheres Passwort für Ihren Vertriebsaccount
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Neues Passwort
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green pr-12"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Passwort bestätigen
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Passwort wiederholen"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
              required
            />
          </div>

          {/* Password strength hints */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-700 mb-2">Passwort-Anforderungen:</div>
            <ul className="space-y-1 text-sm">
              <li className={`flex items-center gap-2 ${password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                <CheckCircle className={`w-4 h-4 ${password.length >= 8 ? 'text-green-500' : 'text-gray-300'}`} />
                Mindestens 8 Zeichen
              </li>
              <li className={`flex items-center gap-2 ${password === confirmPassword && password.length > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                <CheckCircle className={`w-4 h-4 ${password === confirmPassword && password.length > 0 ? 'text-green-500' : 'text-gray-300'}`} />
                Passwörter stimmen überein
              </li>
            </ul>
          </div>

          <Button
            type="submit"
            disabled={loading || !token}
            className="w-full bg-brand-green hover:bg-brand-green-dark text-white py-3"
          >
            {loading ? (
              <Loader className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              'Passwort setzen & Account aktivieren'
            )}
          </Button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          Bei Problemen wenden Sie sich an Ihren Administrator.
        </p>
        </div>
      </div>
    </div>
  );
};

export default VertriebSetPasswordPage;
